import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, Search, Filter } from 'lucide-react';

const RealisationsAdminPage = () => {
  const [realisations, setRealisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    fetchRealisations();
  }, [filterStatus, filterCategory]);

  const fetchRealisations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterCategory) params.append('category', filterCategory);

      const response = await fetch(
        `${BACKEND_URL}/api/realisations/admin/all?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      const data = await response.json();
      setRealisations(data.realisations || []);
    } catch (error) {
      console.error('Erreur chargement réalisations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette réalisation ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/realisations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchRealisations();
        alert('Réalisation supprimée avec succès');
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const filteredRealisations = realisations.filter(r =>
    r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      published: 'bg-green-100 text-green-800',
      draft: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      published: 'Publiée',
      draft: 'Brouillon',
      archived: 'Archivée'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#020d2a] text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Gestion des Réalisations</h1>
              <p className="text-gray-300 mt-1">{realisations.length} réalisation(s)</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg transition-colors"
              >
                ← Retour Dashboard
              </button>
              <button
                onClick={() => navigate('/admin/realisations/create')}
                className="bg-[#FF6600] hover:bg-[#e55a00] px-6 py-2 rounded-lg font-semibold flex items-center gap-2 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Nouvelle Réalisation
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="bg-white border-b py-4 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-4">
            {/* Recherche */}
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par titre ou ville..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                />
              </div>
            </div>

            {/* Filtre statut */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
            >
              <option value="">Tous les statuts</option>
              <option value="published">Publiée</option>
              <option value="draft">Brouillon</option>
              <option value="archived">Archivée</option>
            </select>

            {/* Filtre catégorie */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
            >
              <option value="">Toutes catégories</option>
              <option value="Ponts de quai">Ponts de quai</option>
              <option value="Niveleurs hydrauliques">Niveleurs hydrauliques</option>
              <option value="Mini niveleurs">Mini niveleurs</option>
              <option value="Butoirs">Butoirs</option>
              <option value="Garde-corps et barrières">Garde-corps et barrières</option>
              <option value="Installation complète">Installation complète</option>
            </select>
          </div>
        </div>
      </div>

      {/* Liste */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#FF6600] mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        ) : filteredRealisations.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-gray-600 mb-4">Aucune réalisation trouvée</p>
            <button
              onClick={() => navigate('/admin/realisations/create')}
              className="bg-[#FF6600] hover:bg-[#e55a00] text-white px-6 py-3 rounded-lg font-semibold"
            >
              Créer la première réalisation
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredRealisations.map((realisation) => (
              <div key={realisation.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6">
                <div className="flex gap-6">
                  {/* Image */}
                  <div className="w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                    <img
                      src={realisation.main_image}
                      alt={realisation.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Contenu */}
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-[#020d2a] mb-1">{realisation.title}</h3>
                        <p className="text-gray-600">
                          {realisation.city} • {realisation.date || '2026'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {getStatusBadge(realisation.status)}
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          {realisation.category}
                        </span>
                      </div>
                    </div>

                    {realisation.equipments && realisation.equipments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {realisation.equipments.slice(0, 4).map((eq, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {eq}
                          </span>
                        ))}
                        {realisation.equipments.length > 4 && (
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            +{realisation.equipments.length - 4}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 mt-4">
                      {realisation.status === 'published' && (
                        <button
                          onClick={() => window.open(`/realisations/${realisation.slug}`, '_blank')}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-100 text-green-700 hover:bg-green-200 rounded transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          Voir
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/admin/realisations/edit/${realisation.id}`)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 rounded transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDelete(realisation.id)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RealisationsAdminPage;
